import { App as CapacitorApp, type URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { publicConfig } from "@/lib/public-config";

export type CheckoutReturnPlatform = "web" | "mobile";

const mobileScheme = publicConfig.mobileAppScheme.replace(/\/+$/, "");

function normalizePath(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function normalizeNativeRoute(pathOrRoute: string) {
  const trimmed = pathOrRoute.trim();
  if (!trimmed) return "/";

  const suffixIndex = trimmed.search(/[?#]/);
  const rawPath = suffixIndex >= 0 ? trimmed.slice(0, suffixIndex) : trimmed;
  const suffix = suffixIndex >= 0 ? trimmed.slice(suffixIndex) : "";

  let nextPath = rawPath || "/";

  if (!nextPath.startsWith("/")) {
    nextPath = `/${nextPath}`;
  }

  while (nextPath === "/app" || nextPath.startsWith("/app/")) {
    nextPath = nextPath === "/app" ? "/" : nextPath.slice(4);
  }

  return `${normalizePath(nextPath)}${suffix}`;
}

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

export function getCheckoutReturnPlatform(): CheckoutReturnPlatform {
  return isNativeApp() ? "mobile" : "web";
}

export function buildNativeAppUrl(pathOrRoute: string) {
  return `${mobileScheme}${normalizeNativeRoute(pathOrRoute)}`;
}

export async function openExternalUrl(url: string) {
  if (!url) return;

  if (isNativeApp()) {
    await Browser.open({
      url,
      presentationStyle: "fullscreen",
    });
    return;
  }

  window.location.assign(url);
}

export async function closeExternalBrowser() {
  if (!isNativeApp()) return;
  await Browser.close().catch(() => undefined);
}

export function buildMobileCheckoutReturnUrl(params: {
  bookingId: string;
  paymentId?: string | null;
  sessionId?: string | null;
  checkoutState?: string | null;
}) {
  const search = new URLSearchParams();
  search.set("booking_id", params.bookingId);

  if (params.paymentId) search.set("payment_id", params.paymentId);
  if (params.sessionId) search.set("session_id", params.sessionId);
  if (params.checkoutState) search.set("checkout_state", params.checkoutState);

  return buildNativeAppUrl(`/bookings/checkout-return?${search.toString()}`);
}

export function normalizeAppUrlToRoute(rawUrl: string) {
  if (!rawUrl) return null;

  try {
    const url = new URL(rawUrl);

    if ((url.protocol === "https:" || url.protocol === "http:") && url.pathname.startsWith("/app/")) {
      return `${normalizePath(url.pathname)}${url.search}${url.hash}`;
    }

    if (rawUrl.startsWith(mobileScheme)) {
      const hostPrefix = url.host === "app" ? "/app" : "";
      const routePath = `${hostPrefix}${normalizePath(url.pathname)}`;
      return `${routePath}${url.search}${url.hash}`;
    }
  } catch {
    return null;
  }

  return null;
}

export async function getLaunchRouteFromNativeUrl() {
  if (!isNativeApp()) return null;

  const launch = await CapacitorApp.getLaunchUrl().catch(() => null);
  return launch?.url ? normalizeAppUrlToRoute(launch.url) : null;
}

export async function registerNativeUrlListener(onRoute: (route: string) => void) {
  if (!isNativeApp()) {
    return () => undefined;
  }

  const listener = await CapacitorApp.addListener("appUrlOpen", async (event: URLOpenListenerEvent) => {
    const route = normalizeAppUrlToRoute(event.url);
    if (!route) return;

    await closeExternalBrowser();
    onRoute(route);
  });

  return () => {
    void listener.remove();
  };
}
